import React, { useState, useEffect } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Button, CircularProgress, TextField, InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { fetchData, postData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { getCurrentMonthRange, isValidDateRange, getCurrentDate } from '../../../utils/dateUtils';
import Pagination from '../../shared/Pagination';
import '../Subcontract/Index.css';


const ServiceUpdateIndex = () => {
    const { token, companyId, isAuthenticated, logout, userId } = useAuth();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedItems, setSelectedItems] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [updateDates, setUpdateDates] = useState({});
    const [expandedPOs, setExpandedPOs] = useState({});
    const [updating, setUpdating] = useState(false);
    
    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    
    // Initialize with current month range
    const defaultRange = getCurrentMonthRange();
    const [fromDate, setFromDate] = useState(defaultRange.fromDate);
    const [toDate, setToDate] = useState(defaultRange.toDate);
    const [dateError, setDateError] = useState('');

    // Validate and correct dates on component mount
    useEffect(() => {
        const currentFromDate = fromDate;
        const currentToDate = toDate;
        
        if (!isValidDateRange(currentFromDate, currentToDate)) {
            console.log('Invalid date range detected, correcting...', { currentFromDate, currentToDate });
            const correctedRange = getCurrentMonthRange();
            setFromDate(correctedRange.fromDate);
            setToDate(correctedRange.toDate);
            setDateError('');
        }
    }, []);

    // Validate dates whenever they change
    useEffect(() => {
        if (fromDate && toDate && !isValidDateRange(fromDate, toDate)) {
            setDateError('Invalid date range: From date cannot be after To date');
        } else if (dateError && fromDate && toDate && isValidDateRange(fromDate, toDate)) {
            setDateError('');
        }
    }, [fromDate, toDate]);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        // Only load data if we have valid dates
        if (isValidDateRange(fromDate, toDate)) {
            fetchStampingUpdates();
        }
    }, [isAuthenticated, token, fromDate, toDate]);

    // Date validation functions
    const handleFromDateChange = (newFromDate) => {
        setDateError('');
        
        if (!isValidDateRange(newFromDate, toDate)) {
            setDateError('From date cannot be after To date');
            return;
        }
        
        if (new Date(newFromDate) > new Date(getCurrentDate())) {
            setDateError('From date cannot be in the future');
            return;
        }
        
        setFromDate(newFromDate);
    };

    const handleToDateChange = (newToDate) => {
        setDateError('');
        
        if (!isValidDateRange(fromDate, newToDate)) {
            setDateError('To date cannot be before From date');
            return;
        }
        
        if (new Date(newToDate) > new Date(getCurrentDate())) {
            setDateError('To date cannot be in the future');
            return;
        }
        
        setToDate(newToDate);
    };

    // Function to fix invalid date ranges
    const fixDateRange = () => {
        const correctedRange = getCurrentMonthRange();
        setFromDate(correctedRange.fromDate);
        setToDate(correctedRange.toDate);
        setDateError('');
    };

    const fetchStampingUpdates = async () => {
        try {
            setLoading(true);
            const url = `/ServiceUpdates/${companyId}?fromDate=${fromDate}&toDate=${toDate}`;
            console.log('Fetching Service Updates with URL:', url);
            const response = await fetchData('ReminderApi', url, token);
            console.log('Service Updates Response:', response);
            if (response && Array.isArray(response)) {
                setData(response);
            } else {
                setData([]);
            }
            setLoading(false);
        } catch (error) {
            console.error('Error fetching service updates:', error);
            setData([]);
            setLoading(false);
        }
    };

    useEffect(() => {
        // Group data by PO Number
        const groupedByPO = {};
        
        data.forEach(item => {
            const poNumber = item.PoNumber || item.poNumber || item.pOnumber || 'Unknown';
            if (!groupedByPO[poNumber]) {
                groupedByPO[poNumber] = {
                    poNumber: poNumber,
                    customer: item.Customer || item.customer || item.CustomerName || item.customername || 'N/A',
                    quarters: {
                        Q1: [],
                        Q2: [],
                        Q3: [],
                        Q4: []
                    }
                };
            }
            
            // Determine quarter based on quarterNo field (1, 2, 3, 4)
            const quarterNum = item.quarterNo || item.QuarterNo || item.quarter || 1;
            const quarter = `Q${quarterNum}`;
            
            // Ensure quarter is valid (Q1, Q2, Q3, Q4)
            if (['Q1', 'Q2', 'Q3', 'Q4'].includes(quarter)) {
                groupedByPO[poNumber].quarters[quarter].push(item);
            } else {
                // Default to Q1 if invalid
                groupedByPO[poNumber].quarters['Q1'].push(item);
            }
        });
        
        // Sort jobs within each quarter: status=2 (Pending) → status=1 (Upcoming) → status=3 (Completed)
        Object.values(groupedByPO).forEach(po => {
            Object.keys(po.quarters).forEach(quarter => {
                po.quarters[quarter].sort((a, b) => {
                    const getStatusPriority = (item) => {
                        // Check if completed first
                        if (item.isService === true || item.IsService === true) return 3;
                        
                        // Then check status field
                        if (item.status === 2) return 1; // Pending - highest priority
                        if (item.status === 1) return 2; // Upcoming - medium priority
                        if (item.status === 3) return 3; // Completed - lowest priority
                        
                        return 2; // Default to Upcoming priority
                    };
                    
                    return getStatusPriority(a) - getStatusPriority(b);
                });
            });
        });
        
        // Filter by search term
        const filtered = Object.values(groupedByPO).filter(po =>
            po.poNumber.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
            po.customer.toLowerCase().includes(searchTerm.toLowerCase())
        );
        
        setFilteredData(filtered);
    }, [data, searchTerm]);
    
    const togglePO = (poNumber) => {
        setExpandedPOs(prev => ({
            ...prev,
            [poNumber]: !prev[poNumber]
        }));
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelectedItems(filteredData.map(item => item.Id || item.id));
        } else {
            setSelectedItems([]);
        }
    };

    const handleSelectItem = (id, jobIndex) => {
        setSelectedItems(prev => {
            const isCurrentlySelected = prev.includes(id);
            
            if (isCurrentlySelected) {
                // Remove from selection and clear date
                setUpdateDates(prevDates => {
                    const dateKey = `${id}-${jobIndex}`;
                    const newDates = { ...prevDates };
                    delete newDates[dateKey];
                    return newDates;
                });
                return prev.filter(itemId => itemId !== id);
            } else {
                // Add to selection and auto-fill current date
                const currentDate = new Date().toISOString().split('T')[0];
                const dateKey = `${id}-${jobIndex}`;
                setUpdateDates(prevDates => ({
                    ...prevDates,
                    [dateKey]: currentDate
                }));
                return [...prev, id];
            }
        });
    };

    const prepareServicePayload = (item, modalUpdateDate, currentUserId) => {
        // Priority: Use the date picked from calendar (modalUpdateDate) if available
        const candidate = modalUpdateDate || item.ServiceUpdateDate || item.serviceUpdateDate || new Date().toISOString().split('T')[0];
        console.log('Service Payload - Item:', item, 'Selected Date:', modalUpdateDate, 'Final Date:', candidate);
        
        // Match the ServiceUpdate model structure exactly - include all required fields
        return {
            Id: item.Id ?? item.id ?? 0,
            CompanyId: item.CompanyId ?? item.companyId ?? companyId,
            PurchaseOrderId: item.PurchaseOrderId ?? item.PurchaseId ?? item.purchaseId ?? 0,
            CustomerId: item.CustomerId ?? item.customerId ?? 0,
            ServiceUpdateDate: candidate,
            IsService: true,
            Description: item.Description ?? "",  // Required field
            IsActive: item.IsActive ?? true,
            CreatedDate: item.CreatedDate ?? new Date().toISOString(),
            CreatedBy: item.CreatedBy ?? currentUserId,
            UpdatedDate: new Date().toISOString(),
            UpdatedBy: currentUserId,
            BillId: item.BillId ?? 0,
            BillNo: item.BillNo ?? "",  // Required field
            IsBilled: item.IsBilled ?? false,
            ReceiptId: item.ReceiptId ?? 0,
            ReceiptNo: item.ReceiptNo ?? "",  // Required field
            IsReceipt: item.IsReceipt ?? false,
            QuarterNo: item.QuarterNo ?? item.quarterNo ?? 0
        };
    };

    const updateServiceApi = async (items) => {
        console.log('Calling Service Update API with items:', items);
        const response = await postData("AMCServiceApi/UpdateList", items, token);
        console.log('Service Update API Response:', response);
        return { success: response?.success === true };
    };

    const handleUpdateSelected = async () => {
        if (selectedItems.length === 0) {
            alert('Please select at least one item to update');
            return;
        }

        // Validate that all selected items have dates
        const missingDates = [];
        selectedItems.forEach(id => {
            // Find the item in the flattened data structure
            let foundItem = null;
            let foundIndex = null;
            
            for (const po of filteredData) {
                for (const [, jobs] of Object.entries(po.quarters)) {
                    const itemIndex = jobs.findIndex(job => (job.Id || job.id) === id);
                    if (itemIndex !== -1) {
                        foundItem = jobs[itemIndex];
                        foundIndex = itemIndex;
                        break;
                    }
                }
                if (foundItem) break;
            }
            
            if (foundItem) {
                const dateKey = `${id}-${foundIndex}`;
                if (!updateDates[dateKey]) {
                    missingDates.push(foundItem);
                }
            }
        });

        if (missingDates.length > 0) {
            alert(`Please select dates for all selected items. ${missingDates.length} item(s) missing dates.`);
            return;
        }

        try {
            setUpdating(true);
            
            // Prepare payload for all selected items
            const payloadItems = [];
            
            selectedItems.forEach(id => {
                // Find the item in the flattened data structure
                let foundItem = null;
                let foundIndex = null;
                
                for (const po of filteredData) {
                    for (const [, jobs] of Object.entries(po.quarters)) {
                        const itemIndex = jobs.findIndex(job => (job.Id || job.id) === id);
                        if (itemIndex !== -1) {
                            foundItem = jobs[itemIndex];
                            foundIndex = itemIndex;
                            break;
                        }
                    }
                    if (foundItem) break;
                }
                
                if (foundItem) {
                    const dateKey = `${id}-${foundIndex}`;
                    const selectedDate = updateDates[dateKey];
                    const payload = prepareServicePayload(foundItem, selectedDate, userId);
                    payloadItems.push(payload);
                }
            });

            console.log('Prepared payloads for bulk update:', payloadItems);

            // Call the API
            const result = await updateServiceApi(payloadItems);

            if (result.success) {
                alert(`Successfully updated ${selectedItems.length} service(s)`);
                
                // Clear selections and dates
                setSelectedItems([]);
                setUpdateDates({});
                
                // Refresh data
                await fetchStampingUpdates();
            } else {
                alert('Failed to update services. Please try again.');
            }
        } catch (error) {
            console.error('Error updating services:', error);
            alert('An error occurred while updating services. Please try again.');
        } finally {
            setUpdating(false);
        }
    };

    const handleDateChange = (itemId, date, index) => {
        setUpdateDates(prev => ({
            ...prev,
            [`${itemId}-${index}`]: date
        }));
    };

    // Pagination logic
    const totalItems = filteredData.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = filteredData.slice(startIndex, endIndex);

    // Reset to first page when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, fromDate, toDate]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleItemsPerPageChange = (newItemsPerPage) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
    };


    return (
        <div className="subcontract-index-wrapper">
            {/* Header */}
            <div className="subcontract-index-header">
                <div className="header-left">
                    <h1 className="index-title">Service Updates</h1>
                    <p className="index-subtitle">Manage service schedules by quarters</p>
                </div>
            </div>

            {/* Filters */}
            <div className="filters-section">
                {dateError && (
                    <div style={{ 
                        color: '#dc2626', 
                        fontSize: '14px', 
                        marginBottom: '8px',
                        padding: '8px 12px',
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <span>{dateError}</span>
                        <Button
                            size="small"
                            onClick={fixDateRange}
                            style={{
                                marginLeft: '12px',
                                fontSize: '12px',
                                padding: '4px 8px',
                                backgroundColor: '#dc2626',
                                color: 'white',
                                textTransform: 'none'
                            }}
                        >
                            Fix Dates
                        </Button>
                    </div>
                )}
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>From:</label>
                        <input
                            type="date"
                            value={fromDate}
                            onChange={(e) => handleFromDateChange(e.target.value)}
                            max={getCurrentDate()}
                            style={{
                                padding: '8px 12px',
                                border: dateError ? '1px solid #dc2626' : '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontSize: '14px',
                                cursor: 'pointer'
                            }}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>To:</label>
                        <input
                            type="date"
                            value={toDate}
                            onChange={(e) => handleToDateChange(e.target.value)}
                            min={fromDate}
                            max={getCurrentDate()}
                            style={{
                                padding: '8px 12px',
                                border: dateError ? '1px solid #dc2626' : '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontSize: '14px',
                                cursor: 'pointer'
                            }}
                        />
                    </div>
                </div>
                <TextField
                    placeholder="Search by PO Number or Customer Name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }
                    }}
                />
                {selectedItems.length > 0 && (
                    <Button
                        variant="contained"
                        onClick={handleUpdateSelected}
                        disabled={updating}
                        style={{
                            background: updating ? '#ccc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            textTransform: 'none',
                            fontWeight: 600,
                            padding: '10px 24px',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                            cursor: updating ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {updating ? (
                            <>
                                <CircularProgress size={16} style={{ marginRight: '8px', color: 'white' }} />
                                Updating...
                            </>
                        ) : (
                            `Update Selected (${selectedItems.length})`
                        )}
                    </Button>
                )}
            </div>

            {/* Table */}
            <div className="table-container">
                {loading ? (
                    <div className="loading-container">
                        <CircularProgress />
                        <p>Loading stamping updates...</p>
                    </div>
                ) : filteredData.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-icon">📋</div>
                        <h3>No Service Updates Found</h3>
                        <p>No service updates are available at this time</p>
                    </div>
                ) : (
                    <TableContainer component={Paper} className="modern-table">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell className="table-header">Sl No</TableCell>
                                    <TableCell className="table-header">PO Number</TableCell>
                                    <TableCell className="table-header">Customer Name</TableCell>
                                    <TableCell className="table-header">Quarters</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedData.map((po, poIndex) => {
                                    const isExpanded = expandedPOs[po.poNumber];
                                    const totalJobs = Object.values(po.quarters).reduce((sum, jobs) => sum + jobs.length, 0);
                                    
                                    return (
                                        <React.Fragment key={po.poNumber}>
                                            {/* PO Row */}
                                            <TableRow 
                                                className="table-row po-row" 
                                                onClick={() => togglePO(po.poNumber)}
                                                style={{ cursor: 'pointer', backgroundColor: isExpanded ? '#f8fafc' : 'white' }}
                                            >
                                                <TableCell className="table-cell">
                                                    <span className="serial-number">{startIndex + poIndex + 1}</span>
                                                </TableCell>
                                                <TableCell className="table-cell">
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <span style={{ fontSize: '16px' }}>{isExpanded ? '▼' : '▶'}</span>
                                                        <span className="subcontract-name">{po.poNumber}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="table-cell">
                                                    <span className="contact-text">{po.customer}</span>
                                                </TableCell>
                                                <TableCell className="table-cell">
                                                    <span className="contact-text">{totalJobs} total jobs</span>
                                                </TableCell>
                                            </TableRow>
                                            
                                            {/* Quarters and Jobs - Show all directly, sorted by status */}
                                            {isExpanded && Object.entries(po.quarters)
                                                .filter(([quarter, jobs]) => jobs.length > 0)
                                                .sort(([quarterA, jobsA], [quarterB, jobsB]) => {
                                                    // Get the highest priority status in each quarter
                                                    const getQuarterPriority = (jobs) => {
                                                        const priorities = jobs.map(job => {
                                                            if (job.isService === true || job.IsService === true) return 3;
                                                            if (job.status === 2) return 1; // Pending
                                                            if (job.status === 1) return 2; // Upcoming
                                                            if (job.status === 3) return 3; // Completed
                                                            return 2;
                                                        });
                                                        return Math.min(...priorities); // Return highest priority (lowest number)
                                                    };
                                                    return getQuarterPriority(jobsA) - getQuarterPriority(jobsB);
                                                })
                                                .map(([quarter, jobs]) => {
                                                
                                                return (
                                                    <React.Fragment key={`${po.poNumber}-${quarter}`}>
                                                        {/* Quarter Row with Date, Status, and Update Date */}
                                                        {jobs.map((item, jobIndex) => {
                                                            // Check if completed
                                                            const isCompleted = item.isService === true || item.IsService === true || item.status === 3;
                                                            
                                                            // Determine status
                                                            let statusText = 'Upcoming';
                                                            let statusClass = 'status-upcoming';
                                                            
                                                            if (isCompleted) {
                                                                statusText = 'Completed';
                                                                statusClass = 'status-completed';
                                                            } else if (item.status !== undefined) {
                                                                if (item.status === 2) {
                                                                    statusText = 'Pending';
                                                                    statusClass = 'status-pending';
                                                                } else if (item.status === 1) {
                                                                    statusText = 'Upcoming';
                                                                    statusClass = 'status-upcoming';
                                                                }
                                                            }
                                                            
                                                            return (
                                                                <TableRow 
                                                                    key={item.Id || item.id || jobIndex} 
                                                                    className="table-row quarter-row"
                                                                >
                                                                    {!isCompleted && (
                                                                        <TableCell className="table-cell" padding="checkbox">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={selectedItems.includes(item.Id || item.id)}
                                                                                onChange={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleSelectItem(item.Id || item.id, jobIndex);
                                                                                }}
                                                                                style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#667eea' }}
                                                                            />
                                                                        </TableCell>
                                                                    )}
                                                                    {isCompleted && <TableCell className="table-cell"></TableCell>}
                                                                    
                                                                    <TableCell className="table-cell" colSpan={3}>
                                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingLeft: '12px' }}>
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                                <span className="quarter-title" style={{ fontSize: '12px' }}>
                                                                                    {quarter} 
                                                                                    {quarter === 'Q1' && ' (Jan - Mar)'}
                                                                                    {quarter === 'Q2' && ' (Apr - Jun)'}
                                                                                    {quarter === 'Q3' && ' (Jul - Sep)'}
                                                                                    {quarter === 'Q4' && ' (Oct - Dec)'}
                                                                                </span>
                                                                               
                                                                            </div>
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                                {isCompleted ? (
                                                                                    <span style={{ 
                                                                                        fontSize: '11px', 
                                                                                        color: '#065f46',
                                                                                        fontWeight: 500,
                                                                                        padding: '5px 8px',
                                                                                        background: '#d1fae5',
                                                                                        borderRadius: '6px',
                                                                                        display: 'inline-block',
                                                                                        minWidth: '95px',
                                                                                        textAlign: 'center'
                                                                                    }}>
                                                                                        {item.serviceUpdateDate || item.ServiceUpdateDate 
                                                                                            ? new Date(item.serviceUpdateDate || item.ServiceUpdateDate).toLocaleDateString('en-GB')
                                                                                            : 'N/A'}
                                                                                    </span>
                                                                                ) : (
                                                                                    <input
                                                                                        type="date"
                                                                                        value={updateDates[`${item.Id || item.id}-${jobIndex}`] || ''}
                                                                                        onChange={(e) => {
                                                                                            e.stopPropagation();
                                                                                            handleDateChange(item.Id || item.id, e.target.value, jobIndex);
                                                                                        }}
                                                                                        onClick={(e) => e.stopPropagation()}
                                                                                        className="date-picker-input"
                                                                                    />
                                                                                )}
                                                                                <span className={`status-badge ${statusClass}`}>
                                                                                    {statusText}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </React.Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </div>

            {/* Pagination */}
            {!loading && filteredData.length > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalItems={totalItems}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                />
            )}

            {/* Summary */}
            {!loading && filteredData.length > 0 && (
                <div className="summary-section">
                    <span className="summary-text">
                        Total: {data.length} service updates
                        {selectedItems.length > 0 && ` • ${selectedItems.length} selected`}
                    </span>
                </div>
            )}
        </div>
    );
};

export default ServiceUpdateIndex;
